// DamagedStock.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  CardBody,
  Input,
  Button,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  Label,
  Alert,
  Badge,
} from "reactstrap";
import { ImDownload } from "react-icons/im";
import { FaFilter, FaPlus } from "react-icons/fa";
import swal from "sweetalert";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

/* === SAME APIS (from ../import) === */
import {
  Delete_Damagedstock,
  Get_Damagedstock,
  Update_Damagedstock,
  _Get,
} from "../import/ApiEndPoint/ApiCalling";
import { Create_Warehouse_List } from "../import/ApiEndPoint/Api";
import { CheckPermission } from "../import/CheckPermission";

/* ===== Minimal inline CSS once ===== */
const ensureCSS = () => {
  if (document.getElementById("damaged-inline-css")) return;
  const s = document.createElement("style");
  s.id = "damaged-inline-css";
  s.textContent = `
    .ds-toolbar .count{ background:#eef2ff; color:#3730a3; font-weight:700; padding:6px 10px; border-radius:999px; }
    .ds-table-wrap{ width:100%; overflow:auto; border:1px solid #e5e7eb; border-radius:14px; background:#fff; }
    .ds-table{ width:100%; border-collapse:collapse; min-width:1000px; }
    .ds-table thead th{
      background:#f4f7f6; color:#0f172a; font-weight:700; text-transform:uppercase; font-size:12px; letter-spacing:.03em;
      border-bottom:1px solid #e5e7eb; padding:6px 8px; position:sticky; top:0; z-index:1; text-align:center; white-space:nowrap;
    }
    .ds-table tbody td{ padding:6px 8px; border-bottom:1px solid #f1f5f9; vertical-align:middle; color:#0f172a; font-size:14px; text-align:center; }
    .dl-menu{ position:relative; }
    .dl-pop{ position:absolute; right:0; top:32px; z-index:5; border:1px solid #0b6bcb; background:#fff; border-radius:10px; overflow:hidden; min-width:130px; box-shadow:0 12px 24px rgba(0,0,0,.12); }
    .dl-item{ padding:10px 12px; cursor:pointer; font-weight:600; letter-spacing:.02em; }
    .dl-item:hover{ background:#f8fafc; }
  `;
  document.head.appendChild(s);
};

const toStr = (v) => (v === null || v === undefined ? "" : String(v));
const get = (obj, path, def = "") =>
  path
    .split(".")
    .reduce((a, k) => (a && a[k] !== undefined ? a[k] : undefined), obj) ?? def;
const fmt2 = (n) => (n || n === 0 ? Number(n).toFixed(2) : "");

/* ===== carefully resolve shapes from both APIs ===== */
const getWarehouseObj = (row) =>
  row?.warehouse || // from Get_Damagedstock normalization
  (row?.warehouseId && { _id: row.warehouseId }) || // sometimes API sends only id
  null;

const getWarehouseId = (row) =>
  get(row, "warehouse._id", null) ||
  row?._id || // when row is spread warehouse document from Create_Warehouse_List
  row?.warehouseId ||
  get(row, "damage.warehouse._id", null) ||
  null;

const getProductObj = (row) =>
  get(row, "damage.productId", null) || row?.productId || null;

const getDamageItem = (row) =>
  get(row, "damage.damageItem", null) || row?.damageItem || null;

/* ================= Component ================= */
export default function DamagedStock() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [downloadOpen, setDownloadOpen] = useState(false);

  const [perm, setPerm] = useState({
    View: true,
    Create: true,
    Delete: true,
    Download: true,
  });

  // Mark Wastage modal state
  const [mwOpen, setMwOpen] = useState(false);
  const [mwRow, setMwRow] = useState(null);
  const [mwStatus, setMwStatus] = useState("");
  const [mwPercent, setMwPercent] = useState("");
  const [mwReason, setMwReason] = useState("");

  // Column chooser
  const [ccOpen, setCcOpen] = useState(false);
  const [selIndex, setSelIndex] = useState(-1);
  const addBuf = useRef([]);

  useEffect(() => ensureCSS(), []);

  /* -------- columns definition (robust getters) -------- */
  const baseCols = useMemo(
    () => [
      { key: "uid", header: "UID", get: (_, i) => i + 1 },
      {
        key: "warehouse",
        header: "WAREHOUSE NAME",
        get: (r) =>
          r?.warehouseName ||
          get(r, "warehouse.warehouseName", "") ||
          get(r, "warehouseName", "") ||
          "-",
      },
      {
        key: "address",
        header: "ADDRESS",
        get: (r) =>
          r?.address ||
          get(r, "warehouse.address", "") ||
          get(r, "address", "") ||
          "-",
      },
      {
        key: "mobile",
        header: "NUMBER",
        get: (r) =>
          r?.mobileNo ||
          get(r, "warehouse.mobileNo", "") ||
          get(r, "mobileNo", "") ||
          "-",
      },
      {
        key: "landline",
        header: "LAND LINE NO.",
        get: (r) =>
          r?.landlineNumber ||
          get(r, "warehouse.landlineNumber", "") ||
          get(r, "landlineNumber", "") ||
          "-",
      },
      {
        key: "hsn",
        header: "PRODUCT HSN CODE",
        get: (r) => get(getProductObj(r), "HSN_Code", "-"),
      },
      {
        key: "product",
        header: "PRODUCTS",
        get: (r) => get(getProductObj(r), "Product_Title", "-"),
      },
      {
        key: "price",
        header: "PRICE",
        get: (r) => get(r, "damage.price", r?.price ?? ""),
        render: (r) => fmt2(get(r, "damage.price", r?.price ?? "")),
      },
      {
        key: "reason",
        header: "REASON",
        get: (r) => get(getDamageItem(r), "reason", "-"),
      },
      {
        key: "qty",
        header: "QTY",
        get: (r) => get(getDamageItem(r), "transferQty", "-"),
      },
      {
        key: "tstatus",
        header: "TYPE STATUS",
        get: (r) => get(getDamageItem(r), "typeStatus", "-"),
      },
      {
        key: "dmg",
        header: "DAMAGE %",
        get: (r) => get(getDamageItem(r), "damagePercentage", "-"),
      },
      {
        key: "action",
        header: "ACTION",
        get: () => "",
        render: (r) => (
          <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
            <Badge
              color="primary"
              style={{ cursor: "pointer" }}
              onClick={() => openWastage(r)}
            >
              Mark Wastage
            </Badge>
            {perm?.Delete && (
              <Badge
                color="danger"
                style={{ cursor: "pointer" }}
                onClick={() => handleDelete(r)}
              >
                Delete
              </Badge>
            )}
          </div>
        ),
      },
    ],
    [perm?.Delete]
  );

  const colMap = useMemo(() => {
    const m = new Map();
    baseCols.forEach((c) => m.set(c.key, c));
    return m;
  }, [baseCols]);

  const [selKeys, setSelKeys] = useState([]);
  useEffect(() => {
    const raw = localStorage.getItem("DamagedStockCols");
    let next = [];
    try {
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed) && parsed.length)
        next = parsed.filter((k) => colMap.has(k));
    } catch {}
    if (!next.length) next = baseCols.map((c) => c.key);
    setSelKeys(next);
  }, [colMap, baseCols]);

  const visCols = useMemo(
    () => selKeys.map((k) => colMap.get(k)).filter(Boolean),
    [selKeys, colMap]
  );

  /* -------- data loading -------- */
  const loadData = async (id, db) => {
    setLoading(true);
    setErr("");
    try {
      const p = CheckPermission("Damaged Stock");
      if (p && typeof p === "object") setPerm((old) => ({ ...old, ...p }));

      // Warehouses -> flatten productItems that already have damageItem
      const wRes = await _Get(Create_Warehouse_List, db);
      const ware = Array.isArray(wRes?.Warehouse) ? wRes.Warehouse : [];
      const flattened = ware.flatMap((wh) =>
        (wh?.productItems || []).map((pi) => ({ ...wh, damage: pi }))
      );
      const fromWarehouse = flattened.filter((x) => !!getDamageItem(x));

      // Damaged items endpoint – keep full shape & attach a stable "damage" object for rendering
      const dRes = await Get_Damagedstock(id);
      const dItems = Array.isArray(dRes?.damageItems) ? dRes.damageItems : [];

      const normalized = dItems.map((d) => ({
        // keep original fields
        ...d,
        // ensure consistent accessors used by the table
        warehouse: d?.warehouse || null,
        damage: {
          price: d?.price ?? get(d, "damage.price", null),
          productId: d?.productId ?? get(d, "damage.productId", null),
          damageItem: d?.damageItem ?? get(d, "damage.damageItem", null),
          _id: d?._id || get(d, "damage._id", undefined),
        },
      }));

      // Prefer showing explicit damaged API first so new records appear immediately
      setRows([...normalized, ...fromWarehouse]);
    } catch (e) {
      console.error(e);
      setErr(
        e?.response?.data?.message ||
          e?.message ||
          "Failed to load damaged stock."
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("userData") || "{}");
    loadData(user?._id, user?.database);
  }, []);

  /* -------- search -------- */
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r, i) =>
      visCols.some((c) => toStr(c.get(r, i)).toLowerCase().includes(q))
    );
  }, [rows, search, visCols]);

  /* -------- Mark Wastage -------- */
  const openWastage = (row) => {
    setMwRow(row);
    setMwStatus("");
    setMwPercent("");
    setMwReason("");
    setMwOpen(true);
  };
  const closeWastage = () => setMwOpen(false);

  const submitWastage = async (e) => {
    e.preventDefault();
    if (!mwRow) return;
    const user = JSON.parse(localStorage.getItem("userData") || "{}");
    const userId = user?._id;
    const damageItemId = get(getDamageItem(mwRow), "_id", null);
    if (!userId || !damageItemId) {
      return swal("Missing data", "Cannot locate damage item.", "error");
    }
    const payload = {
      typeStatus: mwStatus,
      demagePercentage: mwPercent,
      reason: mwReason,
    };
    try {
      await Update_Damagedstock(userId, damageItemId, payload);
      swal("Success", "Wastage marked successfully.", "success");
      closeWastage();
      await loadData(userId, user?.database);
    } catch (err) {
      console.error(err);
      swal(
        "Error",
        err?.response?.data?.message || "Failed to update",
        "error"
      );
    }
  };

  /* -------- Delete -------- */
  const handleDelete = async (row) => {
    // resolve IDs across shapes
    const wid = getWarehouseId(row);
    const productObj = getProductObj(row);
    const pid =
      get(row, "damageItem.productId._id", null) ||
      get(productObj, "_id", null) ||
      row?.productId; // fallback if API returns just ID

    if (!wid || !pid) {
      return swal(
        "Missing data",
        "Cannot locate warehouse or product.",
        "error"
      );
    }
    const ok = await swal(
      "Warning",
      "Sure you want to delete this damaged record?",
      {
        buttons: {
          cancel: "Cancel",
          catch: { text: "Delete", value: "delete" },
        },
      }
    );
    if (ok !== "delete") return;
    try {
      await Delete_Damagedstock(wid, pid);
      swal("Deleted", "Record removed.", "success");
      const user = JSON.parse(localStorage.getItem("userData") || "{}");
      await loadData(user?._id, user?.database);
    } catch (err) {
      console.error(err);
      swal(
        "Error",
        err?.response?.data?.message || "Failed to delete",
        "error"
      );
    }
  };

  /* -------- column chooser -------- */
  const openChooser = () => {
    addBuf.current = [];
    setSelIndex(-1);
    setCcOpen(true);
  };
  const closeChooser = () => setCcOpen(false);
  const handleAvailToggle = (checked, key) => {
    const buf = addBuf.current.slice();
    if (checked) {
      if (!buf.includes(key)) buf.push(key);
    } else {
      const i = buf.indexOf(key);
      if (i >= 0) buf.splice(i, 1);
    }
    addBuf.current = buf;
  };
  const removeAt = () => {
    if (selIndex < 0) return;
    const next = selKeys.slice();
    next.splice(selIndex, 1);
    setSelKeys(next);
    setSelIndex(-1);
  };
  const moveUp = () => {
    if (selIndex <= 0) return;
    const next = selKeys.slice();
    const el = next.splice(selIndex, 1)[0];
    next.splice(selIndex - 1, 0, el);
    setSelKeys(next);
    setSelIndex(selIndex - 1);
  };
  const moveDown = () => {
    if (selIndex < 0 || selIndex >= selKeys.length - 1) return;
    const next = selKeys.slice();
    const el = next.splice(selIndex, 1)[0];
    next.splice(selIndex + 1, 0, el);
    setSelKeys(next);
    setSelIndex(selIndex + 1);
  };
  const applyCols = (e) => {
    e.preventDefault();
    localStorage.setItem("DamagedStockCols", JSON.stringify(selKeys));
    closeChooser();
  };

  /* -------- exports -------- */
  const exportPDF = async () => {
    const header = visCols.map((c) => c.header);
    const body = filtered.map((r, idx) =>
      visCols.map((c) => {
        const v = c.render
          ? c.render(r, idx)?.props?.children ?? c.get(r, idx)
          : c.get(r, idx);
        return toStr(v);
      })
    );

    const jsPDF = (await import("jspdf")).default;
    await import("jspdf-autotable");
    const doc = new jsPDF("landscape");
    doc.text("Damaged Stock", 14, 16);
    doc.autoTable({ head: [header], body, startY: 20 });
    doc.save("DamagedStock.pdf");
  };

  const exportXLSX = () => {
    const data = filtered.map((r, idx) => {
      const obj = {};
      visCols.forEach((c) => {
        const v = c.render
          ? c.render(r, idx)?.props?.children ?? c.get(r, idx)
          : c.get(r, idx);
        obj[c.header] = toStr(v);
      });
      return obj;
    });
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buf], { type: "application/octet-stream" }),
      "DamagedStock.xlsx"
    );
  };

  /* -------- loading -------- */
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary">
          Loading...
        </Spinner>
      </div>
    );
  }

  const noData = !filtered.length;

  return (
    <>
      {err && (
        <Row>
          <Col sm="12">
            <Alert color="danger" style={{ marginBottom: 10 }}>
              {err}
            </Alert>
          </Col>
        </Row>
      )}

      {/* ===== Toolbar ===== */}
      <Row className="ds-toolbar" style={{ marginBottom: 10 }}>
        <Col sm="12">
          <Card
            style={{
              padding: 8,
              borderRadius: 14,
              border: "1px solid #e5e7eb",
              background: "#f4f7f6",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: 12,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  fontWeight: 800,
                  textTransform: "uppercase",
                  fontSize: 18,
                }}
              >
                Damaged Stock <span className="count">{filtered.length}</span>
              </div>

              <div style={{ flex: "1 1 240px" }} />

              <div
                style={{
                  minWidth: 260,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Input
                  placeholder="search Item here..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="input-color"
                />
              </div>

              {perm?.Create && (
                <Button
                  color="primary"
                  onClick={() => navigate("/app/Jupitech/warehouse/AddDamage")}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    fontWeight: 600,
                  }}
                >
                  <FaPlus size={12} /> Damage
                </Button>
              )}

              {perm?.View && (
                <Button
                  outline
                  color="primary"
                  onClick={openChooser}
                  title="filter column"
                >
                  <FaFilter size={14} />
                </Button>
              )}

              {perm?.Download && (
                <div
                  className="dl-menu"
                  onMouseEnter={() => setDownloadOpen(true)}
                  onMouseLeave={() => setDownloadOpen(false)}
                >
                  <ImDownload
                    style={{ cursor: "pointer" }}
                    title="download file"
                    size={20}
                    color="#39cccc"
                  />
                  {downloadOpen && (
                    <div className="dl-pop">
                      <div className="dl-item" onClick={exportPDF}>
                        .PDF
                      </div>
                      <div className="dl-item" onClick={exportXLSX}>
                        .XLSX
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </Card>
        </Col>
      </Row>

      {/* ===== Table ===== */}
      <Row>
        <Col sm="12">
          <Card
            style={{
              padding: 8,
              borderRadius: 14,
              border: "1px solid #e5e7eb",
            }}
          >
            {noData ? (
              <div
                style={{ padding: 24, textAlign: "center", color: "#6b7280" }}
              >
                No records found.
              </div>
            ) : (
              <div className="ds-table-wrap">
                <table className="ds-table">
                  <thead>
                    <tr>
                      {visCols.map((c) => (
                        <th key={c.key}>{c.header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((r, idx) => (
                      <tr
                        key={
                          r?._id ||
                          get(r, "damage._id", "") ||
                          `${get(getProductObj(r), "_id", "")}-${idx}`
                        }
                      >
                        {visCols.map((c) => (
                          <td key={c.key}>
                            {typeof c.render === "function"
                              ? c.render(r, idx)
                              : toStr(c.get(r, idx))}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* ===== Column Chooser ===== */}
      <Modal isOpen={ccOpen} toggle={closeChooser} style={{ maxWidth: 1050 }}>
        <ModalHeader toggle={closeChooser}>Change Fields</ModalHeader>
        <ModalBody>
          <Row>
            <Col lg="4" md="4" sm="12">
              <h5>Available Columns</h5>
              <div
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 10,
                  maxHeight: 280,
                  overflow: "auto",
                  background: "#fff",
                  padding: 8,
                }}
              >
                {baseCols.map((col) => (
                  <div
                    key={col.key}
                    style={{
                      padding: "8px 10px",
                      borderRadius: 8,
                      border: "1px solid #e5e7eb",
                      marginBottom: 6,
                      background: "#fff",
                    }}
                  >
                    <label
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <input
                        type="checkbox"
                        onChange={(e) => {
                          const buf = addBuf.current.slice();
                          if (e.target.checked) {
                            if (!buf.includes(col.key)) buf.push(col.key);
                          } else {
                            const i = buf.indexOf(col.key);
                            if (i >= 0) buf.splice(i, 1);
                          }
                          addBuf.current = buf;
                        }}
                      />
                      <span>{col.header}</span>
                    </label>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 8 }}>
                <Button
                  size="sm"
                  color="primary"
                  onClick={() =>
                    setSelKeys(
                      Array.from(new Set([...selKeys, ...addBuf.current]))
                    )
                  }
                >
                  Add Selected →
                </Button>
              </div>
            </Col>

            <Col lg="8" md="8" sm="12">
              <h5>Visible Columns</h5>
              <div
                style={{ display: "flex", gap: 12, alignItems: "flex-start" }}
              >
                <div
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 10,
                    maxHeight: 280,
                    overflow: "auto",
                    background: "#fff",
                    padding: 8,
                    flex: 1,
                  }}
                >
                  {selKeys.map((k, i) => {
                    const col = colMap.get(k);
                    if (!col) return null;
                    return (
                      <div
                        key={k}
                        onClick={() => setSelIndex(i)}
                        style={{
                          padding: "8px 10px",
                          borderRadius: 8,
                          border: "1px solid #e5e7eb",
                          marginBottom: 6,
                          background: i === selIndex ? "#1877f2" : "#fff",
                          color: i === selIndex ? "#fff" : "#000",
                          cursor: "pointer",
                        }}
                      >
                        {col.header}
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: "grid", gap: 8 }}>
                  <Button size="sm" onClick={moveUp}>
                    ↑
                  </Button>
                  <Button size="sm" onClick={moveDown}>
                    ↓
                  </Button>
                  <Button size="sm" color="danger" onClick={removeAt}>
                    Remove
                  </Button>
                </div>
              </div>
              <div style={{ marginTop: 12, textAlign: "center" }}>
                <Button color="primary" onClick={applyCols}>
                  Submit
                </Button>
              </div>
            </Col>
          </Row>
        </ModalBody>
      </Modal>

      {/* ===== Mark Wastage (NO CustomInput used) ===== */}
      <Modal isOpen={mwOpen} toggle={closeWastage} style={{ maxWidth: 900 }}>
        <ModalHeader toggle={closeWastage}>Mark Wastage</ModalHeader>
        <ModalBody>
          <form onSubmit={submitWastage}>
            <Row>
              <Col lg="4" md="4" sm="12">
                <Label>Change Status</Label>
                <Input
                  type="select"
                  value={mwStatus}
                  onChange={(e) => setMwStatus(e.target.value)}
                  required
                >
                  <option value="">--select--</option>
                  <option value="Wastage">Wastage</option>
                </Input>
              </Col>
              <Col lg="4" md="4" sm="12">
                <Label>Damage Percentage</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={mwPercent}
                  onChange={(e) => setMwPercent(e.target.value)}
                  required
                />
              </Col>
              <Col lg="4" md="4" sm="12">
                <Label>Reason</Label>
                <Input
                  type="text"
                  value={mwReason}
                  onChange={(e) => setMwReason(e.target.value)}
                  required
                />
              </Col>
            </Row>
            <div style={{ marginTop: 16, textAlign: "center" }}>
              <Button color="primary" type="submit">
                Submit
              </Button>
            </div>
          </form>
        </ModalBody>
      </Modal>
    </>
  );
}
